import React, { type SyntheticEvent, useCallback } from 'react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItem,
	type CustomTriggerProps,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	AnalyticsEventToProps,
} from '@atlassian/jira-product-analytics-bridge';
import commonMessages from '@atlassian/jira-servicedesk-common/src/i18n';
import messages from './messages';

export type Props = {
	onDelete: () => void;
	onDuplicate: () => void;
	onViewLog: () => void;
	intl: IntlShape;
};

const DropdownItemWithAnalytics = AnalyticsEventToProps('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

const ActionsMenu = ({
	onDelete: onDeleteProp = noop,
	onDuplicate: onDuplicateProp = noop,
	onViewLog: onViewLogProp = noop,
	intl,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onDuplicate = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(_: SyntheticEvent<Record<any, any>>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'duplicate');
			onDuplicateProp();
		},
		[onDuplicateProp],
	);

	const onDelete = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(_: SyntheticEvent<Record<any, any>>, analyticsEvent: UIAnalyticsEvent) => {
			// We need `setTimeout` here to delay the showing of the delete dialog a little bit. Otherwise, the Enter keydown
			// event on the `Delete` meatball action will also affect the auto-focused confirm button in the delete dialog.
			// As a result, the delete dialog with be confirmed immediately after it's shown.
			setTimeout(() => onDeleteProp(), 0);
			fireUIAnalytics(analyticsEvent, 'delete');
		},
		[onDeleteProp],
	);

	const onViewLog = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(_: SyntheticEvent<Record<any, any>>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'viewLog');
			onViewLogProp();
		},
		[onViewLogProp],
	);

	const onMenuOpenChange = useCallback(
		({ isOpen }: OnOpenChangeArgs): void => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'toggled',
				actionSubject: 'dropdownMenu',
			});

			fireUIAnalytics(analyticsEvent, 'ruleOptions', { isOpen });
		},
		[createAnalyticsEvent],
	);

	const { formatMessage } = intl;

	return (
		<DropdownMenu
			onOpenChange={onMenuOpenChange}
			placement="bottom-end"
			// eslint-disable-next-line jira/react-no-inline-function-prop
			trigger={({ triggerRef, ...props }: CustomTriggerProps) => (
				<Button
					{...props}
					appearance="subtle"
					spacing="compact"
					iconBefore={<MoreIcon label={formatMessage(commonMessages.actions)} size="medium" />}
					ref={triggerRef}
				/>
			)}
		>
			<DropdownItemWithAnalytics onClick={onViewLog}>
				{formatMessage(messages.viewLog)}
			</DropdownItemWithAnalytics>
			<DropdownItemWithAnalytics onClick={onDuplicate}>
				{formatMessage(commonMessages.duplicate)}
			</DropdownItemWithAnalytics>
			<DropdownItemWithAnalytics onClick={onDelete}>
				{formatMessage(commonMessages.delete)}
			</DropdownItemWithAnalytics>
		</DropdownMenu>
	);
};

export default injectIntl(ActionsMenu);
